import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, CheckCircle, AlertTriangle, MapPin, User,
  Camera, Upload, FileText, Filter, X, Save, Search, Eye, Download
} from 'lucide-react';
import * as XLSX from 'xlsx';

interface CameraFault {
  id: number;
  camera_name: string;
  camera_brand?: string;
  camera_model?: string;
  camera_screenshot_path?: string | null;
  camera_ip?: string;
  channel_number?: number;
  nvr_name?: string;
  ip_address?: string;
  location_name?: string;
  location_address?: string;
  fault_description: string;
  image_path?: string | null;
  reported_by_user_name?: string;
  reported_by_email?: string;
  reported_at: string;
  status: 'açık' | 'inceleniyor' | 'çözümlendi' | 'kapalı';
  supplier_solution?: string;
  supplier_completed_at?: string;
  notes?: string;
}

interface FaultHistoryAction {
  id: number;
  action_type: string;
  old_status?: string;
  new_status?: string;
  description?: string;
  solution_text?: string;
  file_path?: string;
  supplier_user_name?: string;
  created_at: string;
}

interface DashboardStats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  location_stats: { location_name: string; fault_count: number }[];
}

const statusColors: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  'açık': { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-100', icon: <AlertTriangle className="w-4 h-4" /> },
  'inceleniyor': { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-100', icon: <Clock className="w-4 h-4" /> },
  'çözümlendi': { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-100', icon: <CheckCircle className="w-4 h-4" /> },
  'kapalı': { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-200 dark:text-gray-100', icon: <CheckCircle className="w-4 h-4" /> }
};

const SupplierCameraFaultsPage: React.FC = () => {
  const [faults, setFaults] = useState<CameraFault[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedFault, setSelectedFault] = useState<CameraFault | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [selectedFaultForHistory, setSelectedFaultForHistory] = useState<CameraFault | null>(null);
  const [faultHistoryActions, setFaultHistoryActions] = useState<FaultHistoryAction[]>([]);
  const navigate = useNavigate();

  const [statusUpdateData, setStatusUpdateData] = useState({
    status: '',
    description: '',
    solution_text: ''
  });

  const [imageUploadData, setImageUploadData] = useState({
    image: null as File | null,
    description: ''
  });

  useEffect(() => {
    const supplierUser = localStorage.getItem('supplier_user');
    if (!supplierUser) {
      navigate('/supplier-login');
      return;
    }
    loadFaults();
    loadStats();
  }, [navigate]);

  const loadFaults = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-camera-faults/assigned-faults`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setFaults(result.data || []);
      } else {
        const error = await response.json();
        alert(error.error || 'Arızalar yüklenemedi');
      }
    } catch (error) {
      console.error('Load faults error:', error);
      alert('Arızalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-camera-faults/dashboard-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setStats(result);
      }
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedFault || !statusUpdateData.status) {
      alert('Lütfen durum seçin');
      return;
    }

    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/supplier-camera-faults/fault/${selectedFault.id}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(statusUpdateData)
        }
      );

      if (response.ok) {
        alert('Durum güncellendi');
        setShowStatusModal(false);
        loadFaults();
        loadStats();
      } else {
        const error = await response.json();
        alert(error.error || 'Durum güncellenemedi');
      }
    } catch (error) {
      console.error('Status update error:', error);
      alert('Durum güncellenirken hata oluştu');
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFault || !imageUploadData.image) {
      alert('Lütfen görsel seçin');
      return;
    }

    try {
      const token = localStorage.getItem('supplier_token');
      const formData = new FormData();
      formData.append('image', imageUploadData.image);
      formData.append('description', imageUploadData.description);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/supplier-camera-faults/fault/${selectedFault.id}/upload-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (response.ok) {
        alert('Görsel yüklendi');
        setShowImageModal(false);
        setImageUploadData({ image: null, description: '' });
        loadFaults();
      } else {
        const error = await response.json();
        alert(error.error || 'Görsel yüklenemedi');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Görsel yüklenirken hata oluştu');
    }
  };

  const fetchFaultHistory = async (fault: CameraFault) => {
    try {
      const token = localStorage.getItem('supplier_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/supplier-camera-faults/fault/${fault.id}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setFaultHistoryActions(result.data?.actions || []);
        setSelectedFaultForHistory(fault);
        setShowHistoryModal(true);
      }
    } catch (error) {
      console.error('Error fetching fault history:', error);
    }
  };

  const calculateDaysBetween = (startDate: string, endDate?: string) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getActionTypeLabel = (actionType: string) => {
    const labels: Record<string, string> = {
      'status_change': 'Durum Değişikliği',
      'image_upload': 'Görsel Yüklendi',
      'solution_added': 'Çözüm Eklendi',
      'note_added': 'Not Eklendi',
      'assigned': 'Tedarikçiye Atandı'
    };
    return labels[actionType] || actionType;
  };

  const filteredFaults = faults.filter(fault => {
    const matchesSearch = !searchTerm || (
      fault.camera_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fault.fault_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fault.location_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'active'
        ? (fault.status !== 'çözümlendi' && fault.status !== 'kapalı')
        : fault.status === statusFilter;
    const matchesLocation = !locationFilter || fault.location_name === locationFilter;
    return matchesSearch && matchesStatus && matchesLocation;
  });

  const exportToExcel = () => {
    const rows = filteredFaults.map(fault => ({
      'No': fault.id,
      'Kamera Adı': fault.camera_name,
      'Marka/Model': [fault.camera_brand, fault.camera_model].filter(Boolean).join(' ') || '',
      'Lokasyon': fault.location_name || '',
      'NVR': fault.nvr_name || '',
      'Kanal': fault.channel_number ?? '',
      'IP Adresi': fault.camera_ip || fault.ip_address || '',
      'Arıza Açıklaması': fault.fault_description,
      'Durum': fault.status === 'açık' ? 'Açık' :
               fault.status === 'inceleniyor' ? 'İnceleniyor' :
               fault.status === 'çözümlendi' ? 'Çözümlendi' : 'Kapalı',
      'Bildiren': fault.reported_by_user_name || '',
      'Bildirim Tarihi': new Date(fault.reported_at).toLocaleDateString('tr-TR'),
      'Notlar': fault.notes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = [
      { wch: 6 }, { wch: 24 }, { wch: 18 }, { wch: 20 }, { wch: 20 },
      { wch: 8 }, { wch: 16 }, { wch: 28 }, { wch: 14 }, { wch: 18 },
      { wch: 14 }, { wch: 30 }
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kamera Arızaları');
    const fileName = `kamera-arizalari-${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-700 dark:bg-gray-900 p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <button
          onClick={() => navigate('/supplier-dashboard')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Dashboard'a Dön
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Kamera Arızaları</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Size atanmış kamera arızalarını yönetin</p>
          </div>
          <button
            onClick={exportToExcel}
            disabled={filteredFaults.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Excel'e Aktar
            {filteredFaults.length > 0 && (
              <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {filteredFaults.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Arıza</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
              </div>
              <Camera className="w-10 h-10 text-gray-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Açık</p>
                <p className="text-3xl font-bold text-red-600">{stats.open}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">İnceleniyor</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.in_progress}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Çözümlendi</p>
                <p className="text-3xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Kamera adı, arıza, lokasyon ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          >
            <option value="active">Aktif Arızalar</option>
            <option value="all">Tüm Durumlar</option>
            <option value="açık">Açık</option>
            <option value="inceleniyor">İnceleniyor</option>
            <option value="çözümlendi">Çözümlendi</option>
            <option value="kapalı">Kapalı</option>
          </select>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          >
            <option value="">Tüm Lokasyonlar</option>
            {stats?.location_stats.map(stat => (
              <option key={stat.location_name} value={stat.location_name}>
                {stat.location_name} ({stat.fault_count})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Faults List */}
      <div className="max-w-7xl mx-auto">
        {filteredFaults.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Arıza bulunamadı</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredFaults.map(fault => {
              const statusStyle = statusColors[fault.status];
              return (
                <div
                  key={fault.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4 gap-4">
                    <div className="flex gap-4 flex-1 min-w-0">
                      {/* Görsel önizleme */}
                      {(fault.camera_screenshot_path || fault.image_path) && (
                        <button
                          onClick={() => setFullscreenImage(
                            fault.camera_screenshot_path
                              ? `/uploads/${fault.camera_screenshot_path}`
                              : `/uploads/${fault.image_path}`
                          )}
                          className="flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:opacity-80 hover:ring-2 hover:ring-purple-400 transition-all"
                          title="Görseli büyüt"
                        >
                          <img
                            src={fault.camera_screenshot_path
                              ? `/uploads/${fault.camera_screenshot_path}`
                              : `/uploads/${fault.image_path}`}
                            alt="Kamera"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {fault.camera_name}
                          </h3>
                          <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                            {statusStyle.icon}
                            {fault.status.charAt(0).toUpperCase() + fault.status.slice(1)}
                          </span>
                        </div>
                        {fault.camera_brand && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {fault.camera_brand} {fault.camera_model}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                      #{fault.id}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{fault.location_name || 'Belirtilmemiş'}</span>
                    </div>
                    {fault.nvr_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">
                          <span className="text-gray-500 dark:text-gray-400 text-xs">NVR:</span> {fault.nvr_name}
                        </span>
                      </div>
                    )}
                    {fault.channel_number != null && (
                      <div className="flex items-center gap-2 text-sm">
                        <Camera className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">
                          <span className="text-gray-500 dark:text-gray-400 text-xs">Kanal:</span> {fault.channel_number}
                        </span>
                      </div>
                    )}
                    {(fault.camera_ip || fault.ip_address) && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="w-4 h-4 flex items-center justify-center text-gray-400 flex-shrink-0 font-mono text-xs font-bold">IP</span>
                        <span className="font-mono text-gray-700 dark:text-gray-300">{fault.camera_ip || fault.ip_address}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{fault.fault_description}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{fault.reported_by_user_name || 'Bilinmeyen'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{new Date(fault.reported_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setSelectedFault(fault);
                        setStatusUpdateData({ status: fault.status, description: '', solution_text: '' });
                        setShowStatusModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      Durum Güncelle
                    </button>
                    <button
                      onClick={() => {
                        setSelectedFault(fault);
                        setShowImageModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      Görsel Yükle
                    </button>
                    <button
                      onClick={() => fetchFaultHistory(fault)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      Geçmiş
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedFault && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Durum Güncelle</h3>
              <button onClick={() => setShowStatusModal(false)}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Durum *
                </label>
                <select
                  value={statusUpdateData.status}
                  onChange={(e) => setStatusUpdateData({ ...statusUpdateData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Durum Seçin</option>
                  <option value="açık">Açık</option>
                  <option value="inceleniyor">İnceleniyor</option>
                  <option value="çözümlendi">Çözümlendi</option>
                  <option value="kapalı">Kapalı</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={statusUpdateData.description}
                  onChange={(e) => setStatusUpdateData({ ...statusUpdateData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder="Durum değişikliği ile ilgili açıklama..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Çözüm Açıklaması (Opsiyonel)
                </label>
                <textarea
                  value={statusUpdateData.solution_text}
                  onChange={(e) => setStatusUpdateData({ ...statusUpdateData, solution_text: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder="Yapılan işlem ve çözüm detayları..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  İptal
                </button>
                <button
                  onClick={handleStatusUpdate}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  <Save className="w-4 h-4" />
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Upload Modal */}
      {showImageModal && selectedFault && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Görsel Yükle</h3>
              <button onClick={() => setShowImageModal(false)}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Görsel *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageUploadData({ ...imageUploadData, image: e.target.files?.[0] || null })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={imageUploadData.description}
                  onChange={(e) => setImageUploadData({ ...imageUploadData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder="Görsel ile ilgili açıklama..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowImageModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  İptal
                </button>
                <button
                  onClick={handleImageUpload}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Upload className="w-4 h-4" />
                  Yükle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fault History Modal */}
      {showHistoryModal && selectedFaultForHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Arıza Detayları</h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {selectedFaultForHistory.camera_name} - {selectedFaultForHistory.location_name}
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Özet Bilgiler */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-primary/10 dark:bg-primary/30 rounded-lg p-4">
                  <div className="text-sm text-primary dark:text-primary font-medium">Çözüm Süresi</div>
                  <div className="text-2xl font-bold text-primary-dark dark:text-accent">
                    {selectedFaultForHistory.supplier_completed_at
                      ? `${calculateDaysBetween(selectedFaultForHistory.reported_at, selectedFaultForHistory.supplier_completed_at)} gün`
                      : 'Devam ediyor'}
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
                  <div className="text-sm text-green-600 dark:text-green-400 font-medium">Durum</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    {selectedFaultForHistory.status === 'çözümlendi' ? 'Çözümlendi' :
                     selectedFaultForHistory.status === 'kapalı' ? 'Kapalı' : selectedFaultForHistory.status}
                  </div>
                </div>
              </div>

              {/* Arıza Bilgileri */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Arıza Açıklaması</h4>
                <p className="text-gray-700 dark:text-gray-300">{selectedFaultForHistory.fault_description}</p>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Bildirim Tarihi: {new Date(selectedFaultForHistory.reported_at).toLocaleDateString('tr-TR', {
                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </div>
              </div>

              {/* Çözüm Açıklaması */}
              {selectedFaultForHistory.supplier_solution && (
                <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Çözüm Açıklaması</h4>
                  <p className="text-green-800 dark:text-green-200">{selectedFaultForHistory.supplier_solution}</p>
                </div>
              )}

              {/* Yapılan İşlemler */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Yapılan İşlemler</h4>
                {faultHistoryActions.length > 0 ? (
                  <div className="space-y-3">
                    {faultHistoryActions.map((action) => (
                      <div key={action.id} className="border-l-4 border-purple-500 pl-4 py-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{getActionTypeLabel(action.action_type)}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(action.created_at).toLocaleDateString('tr-TR', {
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                        {action.old_status && action.new_status && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {action.old_status} → {action.new_status}
                          </div>
                        )}
                        {action.description && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{action.description}</p>
                        )}
                        {action.solution_text && (
                          <p className="text-sm text-green-700 dark:text-green-300 mt-1">{action.solution_text}</p>
                        )}
                        {action.supplier_user_name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">İşlemi yapan: {action.supplier_user_name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Henüz kayıtlı işlem bulunmuyor.</p>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 dark:bg-gray-800">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tam Ekran Görsel */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-[70] p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-3 right-3 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 z-10"
            >
              <X className="w-5 h-5 text-gray-800 dark:text-gray-200" />
            </button>
            <img
              src={fullscreenImage}
              alt="Kamera Görüntüsü"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierCameraFaultsPage;
